import { useState } from 'react';
import { Panel } from '../../components/Panel';
import { Button } from '../../components/Button';
import { Spinner, ErrorBox } from '../../components/Spinner';
import { useApi } from '../../hooks/useApi';
import { listUsers, setUserRole, setUserEnabled, deleteUser } from '../../api/endpoints';
import { ROLES, ROLE_LABEL, ROLE_COLOR, RolePermissionTable } from './roles';
import { formatJST } from '../../utils/format';

// サマリに出す件数バッジ（承認待ちを含む）
const SUMMARY_ROLES = [...ROLES, { value: 'pending', label: '承認待ち' }];

// 管理者向けユーザー管理画面（タブ/ルートは admin 限定で到達）
export function UsersPage() {
  return (
    <>
      <AdminPanel />
      <RolePermissionTable />
    </>
  );
}

function AdminPanel() {
  const users = useApi(listUsers, []);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editRole, setEditRole] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState('');

  const all = users.data?.items ?? [];

  function notify(msg: string) {
    setMessage(msg);
    setTimeout(() => setMessage(''), 2000);
  }

  async function run(action: () => Promise<unknown>, okMsg: string) {
    try {
      await action();
      users.refetch();
      notify(okMsg);
    } catch (e) {
      alert((e as Error).message);
    }
  }

  const filtered = all.filter((u) => {
    if (filterRole && u.role !== filterRole) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        u.email.toLowerCase().includes(q) ||
        u.display_name.toLowerCase().includes(q) ||
        u.staff_code.includes(q) ||
        u.staff_name.includes(q)
      );
    }
    return true;
  });

  const roleCounts = all.reduce(
    (acc, u) => {
      acc[u.role] = (acc[u.role] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  // 管理者権限が無い場合（直URLアクセス等）は一覧APIが 403 を返す
  if (users.error) {
    const forbidden = /\b403\b/.test(users.error.message);
    return (
      <Panel title="ユーザー管理">
        {forbidden ? (
          <div className="bg-amber-50 text-amber-800 px-3 py-2 rounded text-sm">
            この画面は管理者のみ利用できます。
          </div>
        ) : (
          <ErrorBox error={users.error} />
        )}
      </Panel>
    );
  }

  return (
    <Panel title="ユーザー管理">
      {/* サマリ */}
      <div className="flex gap-4 mb-4">
        <div className="bg-slate-50 rounded px-4 py-2 text-center">
          <div className="text-2xl font-bold">{all.length}</div>
          <div className="text-xs text-muted">全ユーザー</div>
        </div>
        {SUMMARY_ROLES.map((r) => (
          <div
            key={r.value}
            className={`rounded px-4 py-2 text-center cursor-pointer ${filterRole === r.value ? 'ring-2 ring-primary' : ''} ${ROLE_COLOR[r.value]}`}
            onClick={() => setFilterRole(filterRole === r.value ? '' : r.value)}
          >
            <div className="text-2xl font-bold">{roleCounts[r.value] || 0}</div>
            <div className="text-xs">{r.label}</div>
          </div>
        ))}
      </div>

      {/* 検索 */}
      <div className="flex gap-2 mb-4">
        <input
          type="text"
          className="border rounded px-3 py-1.5 text-sm flex-1"
          placeholder="メール・名前・担当者コードで検索..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {filterRole && (
          <Button variant="ghost" onClick={() => setFilterRole('')}>
            フィルタ解除
          </Button>
        )}
      </div>

      {message && (
        <div className="bg-green-50 text-green-800 px-3 py-2 rounded text-sm mb-3">{message}</div>
      )}

      {users.loading && <Spinner />}

      {/* ユーザー一覧 */}
      <div className="overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left pb-2 pr-3">ユーザー</th>
              <th className="text-left pb-2 pr-3">表示名</th>
              <th className="text-left pb-2 pr-3">担当者</th>
              <th className="text-left pb-2 pr-3">ロール</th>
              <th className="text-left pb-2 pr-3">状態</th>
              <th className="text-left pb-2 pr-3">初回ログイン</th>
              <th className="text-left pb-2 pr-3">最終ログイン</th>
              <th className="pb-2"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} className={`border-b last:border-0 ${!u.enabled ? 'opacity-50' : ''}`}>
                <td className="py-2.5 pr-3 text-xs font-mono">{u.email || u.display_name || u.id}</td>
                <td className="py-2.5 pr-3">{u.display_name}</td>
                <td className="py-2.5 pr-3">
                  <span className="font-mono text-xs">
                    {u.staff_code ? `${u.staff_code} ${u.staff_name}` : '-'}
                  </span>
                </td>
                <td className="py-2.5 pr-3">
                  {editingId === u.id ? (
                    <select
                      className="border rounded px-2 py-0.5 text-xs"
                      value={editRole}
                      onChange={(e) => setEditRole(e.target.value)}
                    >
                      {ROLES.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className={`inline-block px-2 py-0.5 rounded text-xs ${ROLE_COLOR[u.role]}`}>
                      {ROLE_LABEL[u.role]}
                    </span>
                  )}
                </td>
                <td className="py-2.5 pr-3">
                  <span
                    className={`inline-block px-2 py-0.5 rounded text-xs cursor-pointer ${u.enabled ? 'bg-green-100 text-green-800' : 'bg-slate-200 text-slate-600'}`}
                    onClick={() => run(() => setUserEnabled(u.id, !u.enabled), '状態を更新しました')}
                    title="クリックで切替"
                  >
                    {u.enabled ? '有効' : '無効'}
                  </span>
                </td>
                <td className="py-2.5 pr-3 text-xs text-muted">{formatJST(u.created_at)}</td>
                <td className="py-2.5 pr-3 text-xs text-muted">{formatJST(u.last_login)}</td>
                <td className="py-2.5">
                  {editingId === u.id ? (
                    <div className="flex gap-1">
                      <Button
                        className="text-xs px-2 py-1"
                        onClick={() =>
                          run(() => setUserRole(u.id, editRole), 'ロールを更新しました').then(() =>
                            setEditingId(null),
                          )
                        }
                      >
                        保存
                      </Button>
                      <Button
                        variant="ghost"
                        className="text-xs px-2 py-1"
                        onClick={() => setEditingId(null)}
                      >
                        取消
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-1">
                      <Button
                        variant="secondary"
                        className="text-xs px-2 py-1"
                        onClick={() => {
                          setEditingId(u.id);
                          setEditRole(u.role === 'pending' ? 'general' : u.role);
                        }}
                      >
                        編集
                      </Button>
                      <Button
                        variant="ghost"
                        className="text-xs px-2 py-1"
                        onClick={() => {
                          const label = u.email || u.display_name || u.id;
                          if (confirm(`${label} を削除しますか？`)) {
                            run(() => deleteUser(u.id), '削除しました');
                          }
                        }}
                      >
                        削除
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {!users.loading && filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="py-6 text-center text-muted text-sm">
                  ユーザーがいません
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
